/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ReactiveFormHelperService } from "app/shared/helpers/reactive-form-helper.service";

@Component({
	selector: 'otp',
	templateUrl: './otp.component.html',
	styleUrls: ['./otp.component.scss'],
	standalone: false
})

export class OtpComponent implements OnInit {
	@Output() submitOtp = new EventEmitter<string>();
	@Output() resendOtp = new EventEmitter<any>();

	otpDataForm: FormGroup;

	@ViewChild('otpDigit1Input', {static:true}) otpDigit1Input: ElementRef;
	@ViewChild('otpDigit2Input', {static:true}) otpDigit2Input: ElementRef;
	@ViewChild('otpDigit3Input', {static:true}) otpDigit3Input: ElementRef;
	@ViewChild('otpDigit4Input', {static:true}) otpDigit4Input: ElementRef;
	@ViewChild('otpDigit5Input', {static:true}) otpDigit5Input: ElementRef;
	@ViewChild('otpDigit6Input', {static:true}) otpDigit6Input: ElementRef;
	@ViewChild('otpSubmitButton', {static:true}) otpSubmitButton: HTMLButtonElement;

	constructor(
		private fb: FormBuilder,
		private formHelperService: ReactiveFormHelperService,
	) {
		// Ideally this would be some form of control array and a constant used to set the number of digits / controls.
		// However, for simplicity and clarity, and in the interest of minimizing development time and complications, we will just repeat the same control manually for now.
		// TODO: [Development][david.parker][2025-05-30] Rework to use form array to avoid manual repetition for each digit.
		this.otpDataForm = fb.group({
			otpDigit1: ['', [Validators.required, Validators.maxLength(1)]],
			otpDigit2: ['', [Validators.required, Validators.maxLength(1)]],
			otpDigit3: ['', [Validators.required, Validators.maxLength(1)]],
			otpDigit4: ['', [Validators.required, Validators.maxLength(1)]],
			otpDigit5: ['', [Validators.required, Validators.maxLength(1)]],
			otpDigit6: ['', [Validators.required, Validators.maxLength(1)]],
		});
	}

	get otpDigit1(): AbstractControl {
		return this.otpDataForm.controls['otpDigit1'];
	}
	get otpDigit2(): AbstractControl {
		return this.otpDataForm.controls['otpDigit2'];
	}
	get otpDigit3(): AbstractControl {
		return this.otpDataForm.controls['otpDigit3'];
	}
	get otpDigit4(): AbstractControl {
		return this.otpDataForm.controls['otpDigit4'];
	}
	get otpDigit5(): AbstractControl {
		return this.otpDataForm.controls['otpDigit5'];
	}
	get otpDigit6(): AbstractControl {
		return this.otpDataForm.controls['otpDigit6'];
	}

	getOtpDigitInputControl(digit: number) {
		switch (digit) {
			case 1:
				return this.otpDigit1Input;
			case 2:
				return this.otpDigit2Input;
			case 3:
				return this.otpDigit3Input;
			case 4:
				return this.otpDigit4Input;
			case 5:
				return this.otpDigit5Input;
			case 6:
				return this.otpDigit6Input;
			default:
				return null;
		}
	}

	onDigitChanged(digit: number) {
		var nextInput = this.getOtpDigitInputControl(digit + 1);
		if(nextInput==null){
			// KS0009243 UAT 11: Then the user will be moved to the Submit OTP button
			this.otpSubmitButton.focus();
		}
		else{
			nextInput.nativeElement.focus();
		}
	}

	
	ngOnInit() {
		this.otpDigit1Input.nativeElement.focus();
	}

	getFullOtpString(): string{
		return this.otpDigit1.value + this.otpDigit2.value + this.otpDigit3.value +
			this.otpDigit4.value + this.otpDigit5.value + this.otpDigit6.value;
	}

	onSubmitOtp(): void {
		if (this.formHelperService.isFormValid(this.otpDataForm)) {
			// We'll fire an event and letting the outer component deal with the submission. Depending on where this is used,
			// we'll probably need to call different endpoints and do different things in response.
			this.submitOtp.emit(this.getFullOtpString());
		}
	}

	onResendOtp(event): void{
		event.preventDefault();
		this.resendOtp.emit();
	}

	public reset(){
		this.otpDataForm.reset();
		this.otpDataForm.markAsPristine();
		this.otpDataForm.markAsUntouched();
		this.otpDigit1Input.nativeElement.focus();
	}

}
